import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { AIProviderKey, AIProvider } from "@/lib/ai/types";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/settings/ai/providers")({
  component: TenantAIProvidersPage,
  head: () => ({ meta: [{ title: "Mis Proveedores AI — Configuración" }] }),
});

const PROVIDER_LABELS: Partial<Record<AIProvider, string>> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  groq: "Groq",
  openrouter: "OpenRouter",
  mistral: "Mistral AI",
  deepseek: "DeepSeek",
};

const BYOK_PROVIDERS: AIProvider[] = ["anthropic", "openai", "gemini", "groq", "openrouter", "mistral", "deepseek"];

interface FormState {
  provider: AIProvider;
  label: string;
  apiKey: string;
}

const EMPTY_FORM: FormState = { provider: "anthropic", label: "", apiKey: "" };

function TenantAIProvidersPage() {
  const tenantId = useWorkspaceTenantId();
  const [keys, setKeys] = useState<AIProviderKey[]>([]);
  const { confirm } = useAppDialogs();
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await fetch("/api/tenant/ai/providers", {
        credentials: "include",
        headers: { "X-Tenant-Id": tenantId },
      });
      const data = await res.json() as { keys: AIProviderKey[] };
      setKeys(data.keys ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [tenantId]);

  async function handleSave() {
    if (!tenantId) return;
    setSaving(true);
    try {
      await fetch("/api/tenant/ai/providers", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json", "X-Tenant-Id": tenantId },
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(key: AIProviderKey) {
    if (!tenantId) return;
    await fetch("/api/tenant/ai/providers", {
      method: "PATCH",
      credentials: "include",
      headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json", "X-Tenant-Id": tenantId },
      body: JSON.stringify({ id: key.id, enabled: !key.enabled }),
    });
    await load();
  }

  async function handleDelete(id: string) {
    if (!tenantId) return;
    if (!(await confirm({ title: "¿Eliminar esta clave?", destructive: true, confirmLabel: "Eliminar" }))) return;
    await fetch(`/api/tenant/ai/providers?id=${id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { ...clientRequestHeaders(tenantId), "X-Tenant-Id": tenantId },
    });
    await load();
  }

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold">Mis claves AI (BYOK)</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Usá tu propia API key. Si no configurás ninguna, Renata usa las claves de Structura.
            Las claves se guardan cifradas y nunca se muestran completas.
          </p>
        </div>
        <Button size="sm" className="shrink-0 gap-2" onClick={() => { setShowForm(true); setForm(EMPTY_FORM); }}>
          <Plus className="h-4 w-4" />
          Agregar
        </Button>
      </div>

      {showForm && (
        <div className="rounded-xl border bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Proveedor</label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.provider}
                onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value as AIProvider }))}
              >
                {BYOK_PROVIDERS.map((p) => (
                  <option key={p} value={p}>{PROVIDER_LABELS[p] ?? p}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Nombre</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Ej: Mi clave Anthropic"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-muted-foreground">API Key</label>
              <input
                type="password"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                placeholder="sk-..."
                value={form.apiKey}
                onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving || !form.label || !form.apiKey}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{k.label}</p>
                <p className="text-xs text-muted-foreground">
                  {PROVIDER_LABELS[k.provider] ?? k.provider} · {k.key_hint ?? "***"}
                </p>
              </div>
              <button onClick={() => handleToggle(k)} title={k.enabled ? "Deshabilitar" : "Habilitar"}>
                {k.enabled
                  ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                  : <XCircle className="h-4 w-4 text-muted-foreground" />}
              </button>
              <button className="text-muted-foreground hover:text-red-600" onClick={() => handleDelete(k.id)}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {keys.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Sin claves propias. Renata usa las claves de Structura.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
